import Domain from 'helpers/Domain';
import { default as FlowModel, FLOW_TYPE } from 'aa/vue/models/FlowModel';
import { USER_LOGIN_TYPE, USER_REG_TYPE } from 'aa/vue/services/Tracking';

export default {
    namespaced: true,

    state: {
        config: new FlowModel(),
        steps: [],
        currentStep: null,
        didCompleteSignIn: false,
        skipNextBundleStep: false,
        paymentComplete: false,

        marquee: {
            messageHeading: '',
            messageBody: ''
        }
    },

    getters: {
        code: ({ config }) => {
            return config.code;
        },

        type: ({ config }) => {
            return config.type;
        },

        origin_url: ({ config }) => {
            return config.origin_url;
        },

        displaySteps: ({ config }) => {
            return config.steps || false;
        },

        afterSignUpUrl: ({ config }) => {
            return config.afterSignUpUrl || config.destinationUrl;
        },

        destinationUrl: ({ config }, getters) => {
            if (Domain.isDomestic() && getters.campaignDestinationURL) {
                return getters.campaignDestinationURL;
            }
            return config.destinationUrl;
        },

        campaignDestinationURL: (state, getters, rootState) => {
            if (getters.isCampaign) {
                return rootState.pageAttributes?.attributes?.destination_url;
            }

            return null;
        },

        isSignInFlow: ({ config }) => {
            return config.type === FLOW_TYPE.SIGNIN;
        },

        isGift: (state, getters) => {
            return getters.type === FLOW_TYPE.GIFT;
        },

        isEdu: ({ config }) => {
            return config.type === FLOW_TYPE.EDU;
        },

        isRendezvous: ({ config }) => {
            return config.rendezvous && typeof config.rendezvous === 'object';
        },
        isReseller: ({ config }) => {
            return FlowModel.isReseller(config.type);
        },

        isNative: ({ config }, getters) => {
            if (!getters.isRendezvous) {
                return false;
            }

            return config.rendezvous.isNative;
        },

        isCampaign: ({ config }) => {
            return config.type.indexOf('campaign_') === 0;
        },

        /**
         * @param state
         * @param getters
         * @returns {unknown | default.getters.userRegType | userRegType | string}
         */
        userLoginType: (state, getters) => {
            return USER_LOGIN_TYPE[getters.type] || getters.userRegType;
        },

        userRegType: (state, getters) => {
            if (state.config.type === FLOW_TYPE.LIVE_TV) {
                return USER_REG_TYPE.LIVE_TV;
            } else if (state.config.type === FLOW_TYPE.GIFT) {
                return USER_REG_TYPE.GIFT;
            } else if (state.config.type === FLOW_TYPE.EDU) {
                return USER_REG_TYPE.EDU;
            } else if (state.config.type === FLOW_TYPE.CBS_SPORTS) {
                return USER_REG_TYPE.CBS_SPORTS;
            } else if (state.config.reg_driver) {
                return state.config.reg_driver;
            }

            return USER_REG_TYPE.STANDARD;
        },

        // add cases as needed
        skipPlan: (state, getters) => {
            return getters.type === FLOW_TYPE.EDU || getters.isNative;
        },

        // add cases as needed
        skipPayment: (state, getters) => {
            return getters.isNative || getters.type === FLOW_TYPE.GIFT || CBS.Registry.brand.name !== 'PPLUS';
        },

        skipBundlePlanStep: (state, getters, rootState, rootGetters) => {
            return Boolean(
                rootGetters.bundleShowtime ||
                    rootGetters['pageAttributes/hideBundlePlanCTAs'] ||
                    state.skipNextBundleStep
            );
        },

        getFirstStepForAuth(state) {
            return state.steps.find((step) => {
                return !step.requireNonAuth;
            });
        },

        getFirstStepForNonAuth(state) {
            return state.steps.find((step) => {
                return !step.requireAuth;
            });
        },

        getNextStep({ steps, currentStep }, getters, rootState) {
            let nextStep = null;
            let isAuth = rootState.user.isLoggedIn;

            for (let i = currentStep + 1; i < steps.length; i++) {
                let step = steps[i];

                if (
                    isAuth &&
                    step.requireNonAuth && // skip sign in/up steps
                    !step.isGhostAndAllowed()
                ) {
                    // do not skip if ghost user is allowed to sign in/up as next step
                    continue;
                }

                if (!isAuth && step.requireAuth) {
                    continue;
                }

                if (step.name === 'BUNDLE_PLAN' && getters.skipBundlePlanStep) {
                    continue;
                }

                nextStep = step;
                break;
            }

            return nextStep;
        },

        getCurrentStep({ steps, currentStep }) {
            return currentStep && steps[currentStep]
                ? steps[currentStep]
                : steps[0];
        },

        paymentFlowComplete: (state) => {
            return state.paymentComplete;
        },

        countedSteps(state) {
            return state.steps.filter((step) => {
                return step.countStep;
            });
        },

        baseURL: ({ config }) => {
            return config.baseUrl;
        }
    },

    mutations: {
        setDeviceInfo(state, { name, partner }) {
            state.config.type = partner;
            state.config.rendezvous.displayName = name;
        },
        setConfig(state, config) {
            state.config = config;
        },
        setActivationCode(state, code) {
            state.config.code = code;
        },
        setSteps(state, steps) {
            state.steps = steps;
        },
        setCurrentStep(state, step) {
            state.currentStep = Math.min(step, state.steps.length - 1);
        },
        setDidCompleteSignIn(state, didCompleteSignIn) {
            state.didCompleteSignIn = didCompleteSignIn;
        },
        setPaymentComplete(state) {
            state.paymentComplete = true;
        },
        setSkipNextBundleStep(state, skip) {
            state.skipNextBundleStep = skip;
        }
    },

    actions: {
        loadStore({ commit }, flowConfig) {
            commit('setConfig', flowConfig);
        },
        skipNextBundleStep(context, skip) {
            context.commit('setSkipNextBundleStep', skip);
        },
        // decrements total step count
        didCompleteSignIn(context) {
            context.commit('setDidCompleteSignIn', true);
        },
        setCurrentStep(context, payload) {
            context.commit('setCurrentStep', payload.step);
        },
        setSteps({ commit }, steps) {
            let stepIndex = 0;

            for (let config of steps) {
                config.stepIndex = stepIndex++; // array index of step, used in setCurrentStep
            }

            commit('setSteps', steps);
        },
        setActivationCode({ commit }, code) {
            commit('setActivationCode', code);
        }
    }
};
